// src/components/VoiceInputButton.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, Check } from '@/components/ui/lucide-memo';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from './ui/tooltip';
// Ensure QuickMenuAction is imported if it's exported from QuickMenuProvider,
// or rely on TypeScript inference if it's used directly.
// For clarity, let's assume QuickMenuAction is exported or use the local definition.
import { QuickMenuAction } from '#types/types';
import { useKernel } from '@/kernel/Kernel';


interface VoiceInputButtonProps {
  onTranscriptUpdate: (transcript: string, isFinal: boolean) => void;
  disabled?: boolean;
  className?: string;
  iconSize?: number;
  iconStrokeWidth?: number;
  iconColor?: string;
  /**
   * Optional custom icon renderer, given the resolved color (accounts for the
   * listening state). Lets callers swap the default stroked Lucide mic for a
   * filled glyph (e.g. the ChatGPT-style hero composer) without affecting other
   * usages. When omitted, the default Lucide Mic is rendered.
   */
  renderIcon?: (color: string) => React.ReactNode;
  currentLanguage: string;
  onSetLanguage: (language: string) => void;
}

declare global {
  interface Window {
    webkitSpeechRecognition: any;
  }
}

const SUPPORTED_LANGUAGES = [
  { code: 'en-US', label: 'English' },
  { code: 'fr-FR', label: 'Français' },
  { code: 'zh-CN', label: '中文简体' },
  { code: 'zh-TW', label: '中文繁體' },
];

const VoiceInputButton: React.FC<VoiceInputButtonProps> = React.memo(({
  onTranscriptUpdate,
  disabled = false,
  className = "h-[30px] w-[30px] flex items-center justify-center rounded-[12px] bg-black",
  iconSize = 24,
  iconStrokeWidth = 2.5,
  iconColor = "#808080",
  renderIcon,
  currentLanguage,
  onSetLanguage
}) => {
  const { kernel } = useKernel();
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const recognitionRef = useRef<any>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const isFirefox = typeof (window as any).InstallTrigger !== 'undefined';
    const hasSpeechRecognition = 'webkitSpeechRecognition' in window;
    if (!isFirefox && hasSpeechRecognition) {
      setIsSupported(true);
    }
  }, []);

  const currentLanguageRef = useRef(currentLanguage);

  const initializeRecognition = useCallback(() => {
    if (!('webkitSpeechRecognition' in window)) {
      console.warn('Speech recognition not supported in this browser.');
      return null;
    }
    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = currentLanguageRef.current;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onstart = () => {
      setIsListening(true);
      kernel.current?.playUISound('CONFIRM', { volume: 0.25 });
    };
    recognition.onend = () => setIsListening(false);
    recognition.onresult = (event: any) => {
      let interimTranscript = '';
      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const transcriptPart = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcriptPart;
          onTranscriptUpdate(finalTranscript, true);
          finalTranscript = '';
        } else {
          interimTranscript += transcriptPart;
          onTranscriptUpdate(interimTranscript, false);
        }
      }
    };
    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        console.warn('Speech recognition permission denied or service unavailable.');
      } else if (event.error === 'language-not-supported') {
        console.error(`The specified language '${currentLanguageRef.current}' is not supported.`);
      }
    };
    return recognition;
  }, [onTranscriptUpdate, kernel]);

  useEffect(() => {
    if (currentLanguage !== currentLanguageRef.current) {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
        recognitionRef.current = null;
      }
      currentLanguageRef.current = currentLanguage;
      if (isListening) {
        setIsListening(false);
      }
    }
  }, [currentLanguage, isListening]);

  const startRecognition = useCallback(() => {
    if (isListening || disabled) return;
    if (!recognitionRef.current) {
      recognitionRef.current = initializeRecognition();
    }
    if (recognitionRef.current) {
      if (recognitionRef.current.lang !== currentLanguageRef.current) {
        recognitionRef.current.lang = currentLanguageRef.current;
      }
      try {
        recognitionRef.current.start();
      } catch (e) {
        console.error("Error starting speech recognition:", e);
        setIsListening(false);
        recognitionRef.current = null;
      }
    }
  }, [isListening, disabled, initializeRecognition]);

  const stopRecognition = useCallback(() => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
  }, [isListening]);
  
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
        recognitionRef.current = null;
      }
    };
  }, []);

  const handleMouseDownOrTouchStart = useCallback((event: React.MouseEvent | React.TouchEvent) => {
    if ('button' in event && event.button !== 0) {
      return; 
    }
    event.preventDefault();
    startRecognition();
  }, [startRecognition]);

  const handleMouseUpOrTouchEnd = useCallback((event: React.MouseEvent | React.TouchEvent) => {
    if ('button' in event && event.button !== 0) {
        return;
    }
    event.preventDefault();
    stopRecognition();
  }, [stopRecognition]);

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    if (isListening || !buttonRef.current) return;

    const menuActions: QuickMenuAction[] = SUPPORTED_LANGUAGES.map(lang => ({
      label: lang.label,
      onClick: () => { // Renamed 'action' to 'onClick'
        onSetLanguage(lang.code);
        kernel.current?.setMenuData(null); // Close menu after selection
      },
      // 'icon' is now a valid property of QuickMenuAction
      icon: lang.code === currentLanguage ? <Check size={16} /> : <div style={{ width: '16px', height: '16px' }}/>, // Placeholder for alignment
      // 'selected' can be used if your CSS for '.blue--selected' needs it, or for other purposes
      selected: lang.code === currentLanguage, 
    }));

    kernel.current?.setMenuData({
      position: { x: event.clientX, y: event.clientY },
      actions: menuActions,
    });
  }, [onSetLanguage, currentLanguage, isListening, kernel]);

  if (!isSupported) {
    return null; 
  }

  return (
    <Tooltip>
      <TooltipContent side='top'>Dictate</TooltipContent>
      <TooltipTrigger asChild>
        <button
          ref={buttonRef}
          type="button"
          className={`${className} ${isListening ? 'ring-2 ring-white/40' : ''}`}
          onMouseDown={handleMouseDownOrTouchStart}
          onMouseUp={handleMouseUpOrTouchEnd}
          onMouseLeave={handleMouseUpOrTouchEnd}
          onTouchStart={handleMouseDownOrTouchStart}
          onTouchEnd={handleMouseUpOrTouchEnd}
          onTouchCancel={handleMouseUpOrTouchEnd}
          onContextMenu={handleContextMenu}
          disabled={disabled}
          aria-pressed={isListening}
          aria-label="Hold to speak, right-click to change language"
        >
          {renderIcon
            ? renderIcon(isListening ? '#FFFFFF' : iconColor)
            : <Mic size={iconSize} strokeWidth={iconStrokeWidth} color={isListening ? '#FFFFFF' : iconColor} />}
        </button>
      </TooltipTrigger>
    </Tooltip>
  );
});

VoiceInputButton.displayName = 'VoiceInputButton';
export default VoiceInputButton;

